/**
 * Dummy module to create SIP messages.
 * JsSIP does not expose interface for custom SIP methods transactions
 * like PUBLISH or NOTIFY.
 * This module compose SIP message to be send by JsSIP transport module.
 *
 * It is OK only to use for the transaction level.
 * There is only bogus Via headers that just try to look like real.
 * Should work for transaction request/response but definitely would not
 * work for sessions/dialogs.
 */
import JsSIP from 'jssip'

class Message {
  /**
   * @param {String} SIP method
   * @param {UA} JsIPS.UA soft phone object
   * @param {URI} JsSIP URI object. Will be used in ruri
   * @param {String} From heade username
   * return Message
   */
  constructor (method, ua, uri, fromUser) {
    this.method = method
    this.ua = ua
    this.uri = uri || ua.configuration.uri.clone() // create a copy of ua uri
    this.fromUser = fromUser || this.uri.user
    this.expires = null
    this.event = 'presence' // default event
    this.body = null
    this.cType = null
    this.headers = null
  }

  toString () {
    let msg = `${this.method} ${this.uri} SIP/2.0\r\n`
    msg += this.Via()
    msg += this.header('Max-Forwards', JsSIP.C.MAX_FORWARDS)
    msg += this.From()
    msg += this.To()
    msg += this.CSeq()
    msg += this.CallID()
    msg += this.header('Contact', `<${this.ua.configuration.contact_uri}>`)
    msg += this.header('Allow', JsSIP.C.ALLOWED_METHODS)
    msg += this.header('Event', this.event)
    msg += this.expires
      ? this.header('Expires', this.expires)
      : ''
    msg += this.header('User-Agent', this.ua.configuration.user_agent || JsSIP.C.USER_AGENT)
    msg += this.cType
      ? this.header('Content-Type', this.cType)
      : ''

    if (this.headers) {
      for (const [key, value] of Object.entries(this.headers)) {
        if (key && value) {
          msg += this.header(key, value)
        }
      }
    }

    if (this.body) {
      msg += this.header('Content-Length', JsSIP.Utils.str_utf8_length(this.body))
      msg += '\r\n'
      msg += this.body
    }
    return msg
  }

  /**
   * Via header
   */
  Via () {
    const branch = `z9hG4bK${Math.floor(Math.random() * 10000000).toString(16)}`
    let via = `SIP/2.0/${this.ua.transport.via_transport} `
    via += `${this.ua.configuration.via_host};branch=${branch}`
    return this.header('Via', via)
  }

  /**
   * From header with init tag
   */
  From () {
    const uri = new JsSIP.URI(this.uri.scheme, this.fromUser, this.uri.host)
    const from = new JsSIP.NameAddrHeader(uri,
      this.ua.configuration.display_name,
      { tag: JsSIP.Utils.newTag() })
    return this.header('From', from)
  }

  /**
   * To header
   */
  To () {
    // const uri = new JsSIP.URI(this.uri.scheme, this.fromUser, this.uri.host)
    return this.header('To', `<${this.uri}>`)
  }

  /**
   * CSeq header
   */
  CSeq () {
    const num = Math.floor(Math.random() * 10000)
    const cseq = `${num} ${this.method}`
    return this.header('CSeq', cseq)
  }

  /**
   * Call-Id header
   */
  CallID () {
    const id = this.ua.configuration.jssip_id + JsSIP.Utils.createRandomToken(15)
    return this.header('Call-ID', id)
  }

  /**
   * Header compose
   */
  header (name, value) {
    return `${name}: ${value}\r\n`
  }

  /**
   * Set XML/PIDF presence body
   */
  publishPresence (open) {
    this.body = `<?xml version="1.0" encoding="UTF-8"?>
<presence xmlns="urn:ietf:params:xml:ns:pidf"
  xmlns:dm="urn:ietf:params:xml:ns:pidf:data-model"
  xmlns:rp="urn:ietf:params:xml:ns:pidf:rpid"
  entity="${this.uri}">
  <tuple id="${Math.random()}">
    <status><basic>${open ? 'open' : 'closed'}</basic></status>
    <rp:class>other</rp:class>
    <note xml:lang="en">CallPanel DnD set</note>
  </tuple>
  <dm:person id="${Math.random()}">
  <rp:activities>
  <rp:${open ? 'online' : 'busy'}/>
  </rp:activities>
  </dm:person>
</presence>`

    this.cType = 'application/pidf+xml'
    this.expires = 3600 * 24 * 30
  }

  /**
   * Publish dialog event
   */
  publishDialog (enabled, user, identity, realmId, headers) {
    const localURI = `sip:${this.fromUser}@${this.uri.host}`
    this.body = `<?xml version="1.0" encoding="UTF-8"?>
<dialog-info xmlns="urn:ietf:params:xml:ns:dialog-info" entity="${identity}" state="full">
  <dialog id="${user}-${realmId}-dnd" direction="recipient">
    <state>${enabled ? 'confirmed' : 'terminated'}</state>
    <remote>
      <identity>${identity}</identity>
      <target uri="${identity}"></target>
    </remote>
    <local>
      <identity>${localURI}</identity>
      <target uri="${localURI}"></target>
    </local>
  </dialog>
</dialog-info>`

    this.event = 'dialog'
    this.cType = 'application/dialog-info+xml'
    // CLUSTER-2972
    // select very big expire to make sure button light is up
    // until it is disabled. Value to 30 days.
    this.expires = 3600 * 24 * 30
    this.headers = headers
  }
}

export default Message
